import { DATE_PATTERN_TOKENS, type DatePatternToken } from '../date-pattern-token';
import { type DatePatternParts } from '../date-pattern-sample';

/**
 * The month and weekday names to render with, in calendar order.
 *
 * Injected rather than derived, which is what keeps this module free of `Intl`:
 * contracts is imported by the api, the admin **and** React Native, and Hermes
 * ships a reduced ICU. Each caller already knows its locale and builds the two
 * lists once.
 */
export interface DatePatternNames {
  /** January … December. */
  months: readonly string[];
  monthsShort: readonly string[];
  /** Sunday … Saturday, matching `DatePatternParts.weekday`. */
  weekdays: readonly string[];
  weekdaysShort: readonly string[];
}

const pad = (value: number): string => (value < 10 ? `0${String(value)}` : String(value));

function substitute(
  token: DatePatternToken,
  parts: DatePatternParts,
  names: DatePatternNames,
): string {
  switch (token) {
    case 'YYYY':
      return String(parts.year);
    case 'YY':
      // The last two digits, not `year - 2000`: a 1998 birth date must read
      // "98", and a subtraction would render it "-2".
      return String(parts.year).slice(-2);
    case 'MMMM':
      return names.months[parts.month - 1] ?? '';
    case 'MMM':
      return names.monthsShort[parts.month - 1] ?? '';
    case 'MM':
      return pad(parts.month);
    case 'M':
      return String(parts.month);
    case 'dddd':
      return names.weekdays[parts.weekday] ?? '';
    case 'ddd':
      return names.weekdaysShort[parts.weekday] ?? '';
    case 'DD':
      return pad(parts.day);
    case 'D':
      return String(parts.day);
  }
}

/** The token starting at `index`, or null — longest-first, per the token order. */
function tokenAt(pattern: string, index: number): DatePatternToken | null {
  return DATE_PATTERN_TOKENS.find((token) => pattern.startsWith(token, index)) ?? null;
}

/** The end of a `[…]` literal starting at `index`, or null when unterminated. */
function literalEnd(pattern: string, index: number): number | null {
  if (pattern[index] !== '[') return null;
  const close = pattern.indexOf(']', index + 1);
  return close === -1 ? null : close;
}

/**
 * Renders a brand date pattern.
 *
 * The one formatter for `brand.dateFormat`, in contracts so the admin preview
 * and whatever eventually renders a member-facing date are the **same** code
 * (U3). The alternative considered and rejected in P10.2c was giving the admin
 * its own dayjs: agreement with the server would then depend on matching a
 * version *and* a plugin list, and the first `Do` would print differently in the
 * preview than in production.
 *
 * Unknown letters cannot appear — `DatePatternSchema` refuses them — but this is
 * total anyway and copies anything it does not recognise, because a preview that
 * throws is worse than a preview that is slightly literal.
 */
export function formatDatePattern(
  pattern: string,
  parts: DatePatternParts,
  names: DatePatternNames,
): string {
  let out = '';

  for (let index = 0; index < pattern.length;) {
    const close = literalEnd(pattern, index);
    if (close !== null) {
      out += pattern.slice(index + 1, close);
      index = close + 1;
      continue;
    }

    const token = tokenAt(pattern, index);
    if (token !== null) {
      out += substitute(token, parts, names);
      index += token.length;
      continue;
    }

    out += pattern[index];
    index += 1;
  }

  return out;
}

/**
 * How many placeholders a pattern will actually substitute.
 *
 * Zero means the pattern renders the same string for every date in history —
 * legal by the grammar (it is all separators, or one big literal), and exactly
 * the mistake a warning belongs on rather than a refusal.
 */
export function countPatternTokens(pattern: string): number {
  let count = 0;

  for (let index = 0; index < pattern.length;) {
    const close = literalEnd(pattern, index);
    if (close !== null) {
      index = close + 1;
      continue;
    }

    const token = tokenAt(pattern, index);
    if (token !== null) {
      count += 1;
      index += token.length;
      continue;
    }

    index += 1;
  }

  return count;
}
